import { backendOnly } from "./frontend-mode";

// Only completed pilot pages are usable on this host; external and mail links remain unchanged.
export const pilotLink = (value: string, base: string): string | null => {
    try {
        // Legacy filenames refer to top-level pages, including from /kunde/<id>.
        const resolvedBase = /^[a-z0-9_-]+\.asp(?:[?#]|$)/i.test(value)
            ? new URL(`/`, base).href : base;
        const url = new URL(value, resolvedBase);
        if (url.origin !== new URL(base).origin) return value;
        const path = url.pathname.toLowerCase();
        if ([`/`, `/2.0`, `/2.0/`, `/default.asp`, `/2.0/default.asp`].includes(path)) {
            const query = new URLSearchParams();
            url.searchParams.forEach((value, key) => query.append(key.toLowerCase(), value));
            if ([`account`, `loggut`].some(key => query.getAll(key).length > 1)) return null;
            const params = new URLSearchParams();
            const account = query.get(`account`);
            if (account) {
                if (![`recover`, `reset`, `invitation`, `email-change`].includes(account)) return null;
                params.set(`account`, account);
            }
            if (query.get(`loggut`) === `LoggUt`) params.set(`Loggut`, `LoggUt`);
            const search = params.toString();
            return `/login${search ? `?${search}` : ``}${url.hash}`;
        }
        if ([`/frmsendpassord.asp`, `/2.0/frmsendpassord.asp`].includes(path)) {
            return `/login?account=recover${url.hash}`;
        }
        if ([`/frmhjem.asp`, `/2.0/frmhjem.asp`].includes(path)) return `/hjem${url.hash}`;
        if ([`/frmdinprofil.asp`, `/2.0/frmdinprofil.asp`].includes(path)) return `/profil${url.hash}`;
        const pages: Record<string, string> = {
            'frmproblemer.asp': `/innloggingshjelp`, 'frmsaldo2.asp': `/avregningsrapport`,
            'frmepostlogg.asp': `/epostlogg`, 'frmperiodelogg.asp': `/periodelogg`
        };
        const name = path.split(`/`).pop() || ``;
        const administration: Record<string, { route: string; keys: string[] }> = {
            "frmrapporter2.asp": { route: "/rapporter", keys: [] },
            "frmkunder.asp": { route: "/kunder", keys: ["id", "q"] },
            "frmmaaleenheter.asp": { route: "/maalere", keys: ["id", "finn"] },
            "frmbrukere.asp": { route: "/brukere", keys: ["id", "ny", "q", "kunde"] },
            "frmanlegg.asp": { route: "/anlegg", keys: ["id", "ny"] },
            "frmmaalertyper.asp": { route: "/maalertyper", keys: ["id", "ny"] },
            "frmvaretyper.asp": { route: "/varer", keys: ["id"] },
            "frmkundekontaktliste.asp": { route: "/kundekontaktliste", keys: ["kunde"] },
            "frmmaalernr.asp": { route: "/maalernummer", keys: ["id"] },
            "frmaktiviteter.asp": {
                route: "/aktiviteter", keys: ["id", "ny", "replyto", "finn", "kat", "eldreenn", "top"]
            },
            "frmansvar.asp": { route: "/ansvar", keys: [] },
            "frmavlesing.asp": { route: "/avlesing", keys: ["kno"] },
            "frmhistorikk.asp": { route: "/historikk", keys: ["kidvalg", "id", "rtffakt"] },
            "frmkorrespondanse.asp": { route: "/korrespondanse", keys: [] },
            "frmkontaktskjema.asp": { route: "/kontakt", keys: [] },
            "frmmeldingsvar.asp": { route: "/meldingsvar", keys: ["id"] },
            "frmsaldo.asp": { route: "/saldo", keys: ["kidvalg"] },
            "rptbeboere.asp": { route: "/beboere", keys: ["mtype"] },
            "frminaktive.asp": { route: "/inaktive", keys: [] },
            "frmmanglendereg.asp": { route: "/manglende-registreringer", keys: [] },
            "rptadvarsler.asp": { route: "/advarsler", keys: ["type", "maalertype"] },
            "frmpriser.asp": { route: "/priser", keys: ["id"] },
            "frmpriserregistrer.asp": { route: "/registrer-priser", keys: [] },
            "frmfellesforbruk2.asp": { route: "/fellesforbruk", keys: [] },
            "frmgjsnforbruk.asp": { route: "/gjennomsnittsforbruk", keys: [] },
            "frmrapportforbruk.asp": { route: "/forbruksrapport", keys: [] },
            "frmstatistikk.asp": { route: "/statistikk",
                keys: ["rapport", "fraaar", "framnd", "tilaar", "tilmnd", "dato"] },
            "bruksrett.asp": { route: "/bruksrett", keys: [] },
            "frmfakturaer.asp": { route: "/fakturaer",
                keys: ["nr", "id", "SeDato", "krediter", "krediterbekreft", "bolk", "nykreditert", "ikkevislinker"] },
            "frmfakturaerepost.asp": { route: "/faktura-epost",
                keys: ["sendbolkepost", "sendfakturaepost", "dato", "bolk", "nr"] },
            "frmfakturaer-lesocr.asp": { route: "/faktura-ocr", keys: [] },
            "frmfakturalinjer.asp": { route: "/fakturalinjer",
                keys: ["tilfaktyear", "tilfaktmonth", "kid", "com", "mid", "price", "use", "year", "month", "txt"] },
            "frmfakturalinjerapport.asp": { route: "/generer-fakturalinjer", keys: ["maaler", "anlegg"] },
            "rptfaktlinjer.asp": { route: "/fakturerte-fakturalinjer", keys: ["periode", "vare", "mtype"] },
            "frmfakturermaalertype.asp": { route: "/fakturer-maalertype", keys: [] },
            "frmmaalertyper-forskudd2.asp": { route: "/forskudd-avgift", keys: [] },
            "frmavregnalle.asp": { route: "/avregn-alle", keys: [] },
            "systeminnstillinger.asp": { route: "/systeminnstillinger", keys: ["id", "ny"] },
            "sql.asp": { route: "/sql", keys: [] },
            "frmmeny.asp": { route: "/meny", keys: [] },
            "frmsendpmliste.asp": { route: "/mottakere",
                keys: ["SendPurr", "SendAdmFast", "RegAdmFast", "SendPurr2", "SendAdmFast2"] },
            "frmsendpm.asp": { route: "/send-melding",
                keys: ["userIds", "HentBrukerId", "HentMeldingId", "sysmld", "direkteutskrift"] },
            "importkamstrup.asp": { route: "/import-kamstrup", keys: [] },
            "frmsalg.asp": { route: "/overforing",
                keys: ["gjennomforid", "sekladd", "customerno", "seskjulte", "semid"] },
            "frmmaalertyper-forskudd.asp": { route: "/forskudd-maalertyper",
                keys: ["fraaar", "framnd", "tilaar", "tilmnd", "faktfraaar", "faktframnd",
                    "fakttilaar", "fakttilmnd", "pris", "paaslag", "vis"] },
            "rptfakturalinjerprperiode.asp": { route: "/fakturalinjer-per-periode",
                keys: ["vare", "anlegg", "aar", "mnd"] }
        };
        if (/^\/(?:pris|vare)\/[1-9][0-9]*$/.test(path)) return path + url.hash;
        if (/^\/kunde\/[1-9][0-9]*$/.test(path)) return path + url.hash;
        const customerLists: Record<string, string> = {
            aktive: `aktive`, inaktive: `inaktive`, fjernvarmelev: `fjernvarmeleverandorer`,
            utbygger: `utbyggere`, test: `test`
        };
        if (Object.values(customerLists).some(slug => path === `/kunder/${slug}`)) return path + url.hash;
        if ((path === `/kunder` || path === `/frmkunder.asp` || path === `/2.0/frmkunder.asp`) &&
            !Array.from(url.searchParams.keys()).some(key => key.toLowerCase() === `id`)) {
            const legacy = url.hash.substring(1);
            const slug = Object.prototype.hasOwnProperty.call(customerLists, legacy) ? customerLists[legacy] : `aktive`;
            return `/kunder/${slug}`;
        }
        const target = administration[name];
        if (target && (path === `/${name}` || path === `/2.0/${name}`)) {
            const params = new URLSearchParams();
            const query = new URLSearchParams();
            url.searchParams.forEach((value, key) => query.append(key.toLowerCase(), value));
            for (const key of target.keys) {
                const values = query.getAll(key.toLowerCase());
                if (values.length > 1 && !(name === `frmsendpm.asp` && key === `userIds`)) return null;
                if (values.length) params.set(key, values.join(`,`));
            }
            if ([`frmpriser.asp`, `frmvaretyper.asp`].includes(name) && params.has(`id`)) {
                const id = params.get(`id`)!;
                if (!/^[0-9]+$/.test(id) || Number(id) <= 0 || Number(id) > 2147483647) return null;
                return `/${name === `frmpriser.asp` ? `pris` : `vare`}/${Number(id)}${url.hash}`;
            }
            const search = params.toString();
            return `${target.route}${search ? `?${search}` : ``}${url.hash}`;
        }
        if ([`brukermanual.asp`, `frmsendpmp.asp`].includes(name) &&
            (path === `/${name}` || path === `/2.0/${name}`)) {
            const query = url.searchParams;
            const keys = name === `brukermanual.asp` ? [`id`] : [`id`, `ny`, `systemnavn`];
            if (keys.some(key => query.getAll(key).length > 1) ||
                keys.filter(key => query.has(key)).length > 1) return null;
            const params = new URLSearchParams();
            if (query.has(`id`)) {
                const id = query.get(`id`)!;
                if (!/^[0-9]+$/.test(id) || Number(id) <= 0 || Number(id) > 2147483647) return null;
                params.set(`id`, String(Number(id)));
            } else if (keys.includes(`systemnavn`) && query.has(`systemnavn`)) {
                const value = query.get(`systemnavn`)!;
                if (!value.trim() || value.length > 50 || /[\u0000-\u001f\u007f]/.test(value)) return null;
                params.set(`systemnavn`, value);
            } else if (keys.includes(`ny`) && query.has(`ny`)) params.set(`ny`, `true`);
            const route = name === `brukermanual.asp` ? `/brukermanual` : `/meldingsmaler`;
            const search = params.toString();
            return `${route}${search ? `?${search}` : ``}${url.hash}`;
        }
        if (name === `frmbrukertyper.asp` && (path === `/${name}` || path === `/2.0/${name}`)) {
            const query = url.searchParams;
            const id = query.get(`id`);
            const create = Boolean(query.get(`ny`));
            if (query.getAll(`id`).length > 1 || query.getAll(`ny`).length > 1 ||
                (create && id !== null)) return null;
            if (id !== null && (!/^\d+$/.test(id) || Number(id) > 2147483647)) return null;
            return `/brukertyper${id !== null ? `?id=${Number(id)}` : create ? `?ny=true` : ``}${url.hash}`;
        }
        if ((path === `/${name}` || path === `/2.0/${name}`) && pages[name]) return pages[name] + url.hash;
        if (name === `rpttotalforbruk.asp` && (path === `/${name}` || path === `/2.0/${name}`)) {
            const date = url.searchParams.get(`dato`);
            if (url.searchParams.getAll(`dato`).length > 1 || url.searchParams.getAll(`sammenlign`).length > 1) {
                return null;
            }
            if (date) {
                const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
                const parsed = new Date(date);
                if (!match || !Number.isFinite(parsed.valueOf()) || parsed.getUTCFullYear() !== Number(match[1]) ||
                    parsed.getUTCMonth() + 1 !== Number(match[2]) || parsed.getUTCDate() !== Number(match[3])) {
                    return null;
                }
            }
            const params = new URLSearchParams();
            if (date) params.set(`dato`, date);
            if (url.searchParams.get(`sammenlign`)) params.set(`sammenlign`, `true`);
            const search = params.toString();
            return `/totalforbruk${search ? `?${search}` : ``}${url.hash}`;
        }
        if ([`frmperiodepriser.asp`, `frmperiodeforbruk.asp`].includes(name) &&
            (path === `/${name}` || path === `/2.0/${name}`)) {
            const params = new URLSearchParams();
            for (const key of [`fra`, `til`, `kidvalg`]) {
                const values = url.searchParams.getAll(key);
                if (values.length > 1) return null;
                const value = values[0];
                if (!value) continue;
                if (key === `kidvalg` ? !/^(0|[1-9]\d*)$/.test(value) || !Number.isSafeInteger(Number(value)) :
                    !/^\d{4}-(0[1-9]|1[0-2])(-\d{2})?$/.test(value) ||
                    (value.length === 10 && !Number.isFinite(Date.parse(value)))) return null;
                params.set(key, value);
            }
            if (name === `frmperiodeforbruk.asp`) {
                const values = url.searchParams.getAll(`sammenlign`);
                if (values.length > 1) return null;
                if (values[0]) params.set(`sammenlign`, `true`);
            }
            const search = params.toString();
            const route = name === `frmperiodepriser.asp` ? `/periodepriser` : `/periodeforbruk`;
            return `${route}${search ? `?${search}` : ``}${url.hash}`;
        }
        if (name === `rptavlesningerprdag.asp` && (path === `/${name}` || path === `/2.0/${name}`)) {
            const params = new URLSearchParams();
            for (const key of [`fra`, `til`]) {
                const values = url.searchParams.getAll(key);
                if (values.length > 1) return null;
                const value = values[0];
                if (!value) continue;
                const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
                const parsed = new Date(value);
                if (!match || !Number.isFinite(parsed.valueOf()) || parsed.getUTCFullYear() !== Number(match[1]) ||
                    parsed.getUTCMonth() + 1 !== Number(match[2]) || parsed.getUTCDate() !== Number(match[3])) {
                    return null;
                }
                params.set(key, value);
            }
            const search = params.toString();
            return `/avlesninger-per-dag${search ? `?${search}` : ``}${url.hash}`;
        }
        return [`/hjem`, `/profil`, `/login`, `/innloggingshjelp`, `/avregningsrapport`, `/rapporter`,
            `/totalforbruk`, `/epostlogg`, `/periodepriser`, `/periodelogg`,
            `/avlesninger-per-dag`, `/periodeforbruk`, `/brukertyper`,
            `/brukermanual`, `/meldingsmaler`, `/kunder`, `/maalere`, `/brukere`, `/anlegg`,
            `/maalertyper`, `/varer`, `/kundekontaktliste`, `/maalernummer`, `/aktiviteter`,
            `/ansvar`, `/avlesing`, `/historikk`, `/korrespondanse`, `/kontakt`, `/meldingsvar`,
            `/saldo`, `/beboere`, `/inaktive`, `/manglende-registreringer`, `/advarsler`,
            `/priser`, `/registrer-priser`, `/fellesforbruk`,
            `/gjennomsnittsforbruk`, `/forbruksrapport`, `/statistikk`, `/bruksrett`,
            `/fakturaer`, `/faktura-epost`, `/faktura-ocr`, `/fakturalinjer`,
            `/generer-fakturalinjer`, `/fakturerte-fakturalinjer`, `/fakturalinjer-per-periode`,
            `/fakturer-maalertype`, `/forskudd-maalertyper`, `/forskudd-avgift`, `/avregn-alle`, `/overforing`,
            `/systeminnstillinger`, `/sql`, `/import-kamstrup`, `/meny`, `/mottakere`, `/send-melding`]
            .includes(path) ? value : null;
    } catch {
        return null;
    }
};

export const adaptPilotLinks = (root: ParentNode) => {
    if (!backendOnly()) return;
    root.querySelectorAll<HTMLAnchorElement>(`a[href]`).forEach(anchor => {
        const href = anchor.getAttribute(`href`) || ``;
        const destination = pilotLink(href, location.href);
        if (destination === null) anchor.replaceWith(document.createTextNode(anchor.textContent || ``));
        else anchor.href = destination;
    });
};
